// React
import React, { useMemo } from "react";

// MUI
import {
  AddCommentOutlined,
  AutoAwesome,
  CloseRounded,
  OpenInFullRounded,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";

// Third party imports
import { useTranslation } from "react-i18next";

// Components
import { NewBadge } from "components/WhatsNew";

import AiConversation from "./AiConversation";

// "Novità" badge in the header until this day
export const AI_ASSISTANT_NEW_UNTIL = "2026-11-30";

function IconAction({ label, onClick, children, disabled }) {
  return (
    <Tooltip title={label} placement="bottom">
      <span>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-40"
        >
          {children}
        </button>
      </span>
    </Tooltip>
  );
}

/**
 * Chat panel of the AI assistant: bottom-right card on desktop, full screen on phones.
 * Not modal (role="complementary", not "dialog": a dialog would switch off the page shortcuts).
 * "Vai in chat" moves the same conversation to the full screen Chat page.
 */
export default function AiPanel({ context, onClose, onNavigate, onOpenChat, inputRef }) {
  const { t } = useTranslation("aiAssistant");
  const panelContext = useMemo(() => ({ ...context, surface: "panel" }), [context]);

  // Esc closes the panel from anywhere inside it (menus and tooltips handle their own Esc first)
  const handlePanelKeyDown = (event) => {
    if (event.key === "Escape" && !event.defaultPrevented) {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
  };

  return (
    <section
      role="complementary"
      aria-label={t("title")}
      onKeyDown={handlePanelKeyDown}
      className="fixed inset-0 z-(--z-fab) flex flex-col bg-white transition-[opacity,transform] duration-150 ease-out starting:translate-y-2 starting:opacity-0 motion-reduce:transition-none sm:inset-auto sm:right-6 sm:bottom-6 sm:h-[min(640px,calc(100dvh-112px))] sm:w-[400px] sm:rounded-2xl sm:border sm:border-gray-200 sm:shadow-popover"
    >
      <AiConversation
        variant="panel"
        context={panelContext}
        onNavigate={onNavigate}
        inputRef={inputRef}
        renderHeader={({ onReset, canReset }) => (
          <header className="flex h-12 shrink-0 items-center gap-2 border-b border-gray-100 pr-2 pl-4">
            <AutoAwesome
              sx={{ fontSize: 18 }}
              className="shrink-0 text-primary"
              aria-hidden="true"
            />
            <h2 className="min-w-0 truncate text-[15px] font-semibold text-gray-900">
              {t("title")}
            </h2>
            <NewBadge until={AI_ASSISTANT_NEW_UNTIL} />
            <div className="ml-auto flex shrink-0 items-center gap-0.5">
              {onOpenChat && (
                <Tooltip title={t("header.openChatHint")} placement="bottom" describeChild>
                  <button
                    type="button"
                    onClick={onOpenChat}
                    className="mr-0.5 inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-[13px] font-medium whitespace-nowrap text-primary-strong transition-colors hover:bg-primary-soft focus-visible:outline-2 focus-visible:outline-primary"
                  >
                    <OpenInFullRounded sx={{ fontSize: 16 }} aria-hidden="true" />
                    {t("header.openChat")}
                  </button>
                </Tooltip>
              )}
              <IconAction
                label={t("header.newConversation")}
                onClick={onReset}
                disabled={!canReset}
              >
                <AddCommentOutlined sx={{ fontSize: 18 }} />
              </IconAction>
              <IconAction label={t("header.close")} onClick={onClose}>
                <CloseRounded sx={{ fontSize: 20 }} />
              </IconAction>
            </div>
          </header>
        )}
        composerTop={
          context?.company_id ? (
            <p className="mb-1.5 truncate px-1 text-[12px] text-gray-500">
              {context.company_name
                ? t("context.client", { name: context.company_name })
                : t("context.clientNoName")}
            </p>
          ) : null
        }
      />
    </section>
  );
}
